import { Button, Card, Flex, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type Props = {
  isPending: boolean
  onEnable: () => void
}

const useStyles = createStyles(({ token }) => ({
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 12,
    paddingBlock: 12
  },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: token.colorPrimaryBg,
    color: token.colorPrimaryText,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  bullets: {
    fontSize: 12,
    color: token.colorTextSecondary,
    maxWidth: 520,
    lineHeight: 1.6
  }
}))

/**
 * Initial-state card shown when an application has no OIDC client yet.
 * Enabling creates a Confidential client with sensible defaults. The
 * client type (Public / Confidential) and all advanced options become
 * editable in the configuration form that appears after creation —
 * which is the only place where Public-client constraints (e.g.
 * redirect URIs) can be entered before they are validated.
 */
export const OidcEnableCard = ({ isPending, onEnable }: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  return (
    <Card variant="outlined" size="small">
      <div className={styles.hero}>
        <span className={styles.badge} aria-hidden>
          <ShieldCheck size={22} />
        </span>

        <Flex vertical gap={4} align="center">
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('oidc.enable_title', 'Enable OpenID Connect')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t(
              'oidc.enable_subtitle',
              'Let this application authenticate users via OIDC.'
            )}
          </Typography.Text>
        </Flex>

        <ul
          className={styles.bullets}
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            textAlign: 'left'
          }}
        >
          <li>
            ·{' '}
            {t(
              'oidc.enable_bullet_default',
              'Starts as a Confidential client (server-to-server) with PKCE enabled.'
            )}
          </li>
          <li>
            ·{' '}
            {t(
              'oidc.enable_bullet_switch',
              'You can switch to a Public client (SPA / mobile / desktop) afterwards by adding redirect URIs in the configuration form.'
            )}
          </li>
          <li>
            ·{' '}
            {t(
              'oidc.enable_bullet_secret',
              'A client secret will be issued once on creation — store it securely.'
            )}
          </li>
        </ul>

        <Button
          size="small"
          type="primary"
          loading={isPending}
          onClick={onEnable}
          style={{ marginTop: 4 }}
        >
          {t('oidc.enable_cta', 'Enable OIDC')}
        </Button>
      </div>
    </Card>
  )
}
